import { ArcLayer } from "@deck.gl/layers";
import { MapboxLayer } from "@deck.gl/mapbox";
import { jsonToGeojson } from "@/utils";

export class MatrixController {
  constructor(parent) {
    const { $store, mapgl } = parent;

    this.parent = parent;
    this.$store = $store;
    this.map = mapgl;

    // modify handlers
    this.zoneMousemoveHandler = this.zoneMousemoveHandler.bind(this);
    this.zoneMouseleaveHandler = this.zoneMouseleaveHandler.bind(this);
    this.zoneClickHandler = this.zoneClickHandler.bind(this);
  }

  // get data helpers
  async getZonesData() {
    const zonesId = this.parent.activeSC.ids["zones"];

    try {
      const url = `objectInfo/${zonesId}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      const geojson = jsonToGeojson(Object.values(data));

      geojson.features = geojson.features.map((f, i) => ({
        ...f,
        id: String(i + 1)
      }));

      return geojson;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async getArcLayersDataById(zoneId) {
    const modelId = this.parent.activeSC.id;

    try {
      const url = `network_matrix/${modelId}?fromzone_id=${zoneId}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });

      return data;
    } catch (error) {
      console.log(error);
    }
  }

  // add layers and handlers helpers
  addZonesMapLayer(data) {
    this.map.addSource("zones", {
      type: "geojson",
      data
    });

    this.map.addLayer({
      id: "zones_borders",
      source: "zones",
      type: "line",
      paint: {
        "line-width": 1,
        "line-color": "#f44949",
        "line-opacity": 0.6
      }
    });

    this.map.addLayer({
      id: "zones",
      source: "zones",
      type: "fill",
      paint: {
        "fill-color": "#f44949",
        "fill-opacity": [
          "case",
          [
            "any",
            ["boolean", ["feature-state", "hover"], false],
            ["boolean", ["feature-state", "active"], false]
          ],
          0.5,
          0.2
        ]
      }
    });
  }

  zoneMousemoveHandler(e) {
    const { hover } = this.$store.state.situationCenter.matrix;

    if (e.features.length > 0) {
      if (hover) {
        this.map.setFeatureState(
          { source: "zones", id: hover },
          { hover: false }
        );
      }

      this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
        field: "hover",
        value: e.features[0].id
      });
      this.map.setFeatureState(
        {
          source: "zones",
          id: this.$store.state.situationCenter.matrix.hover
        },
        { hover: true }
      );
    }
  }

  zoneMouseleaveHandler() {
    const { hover } = this.$store.state.situationCenter.matrix;

    if (hover) {
      this.map.setFeatureState(
        { source: "zones", id: hover },
        { hover: false }
      );
    }

    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "hover",
      value: null
    });
  }

  async zoneClickHandler(e) {
    const { active } = this.$store.state.situationCenter.matrix;
    const [feature] = e.features;
    const { properties } = feature;
    const id = feature.id;

    if (id === active) {
      if (this.map.getLayer("matrix_arcs")) this.map.removeLayer("matrix_arcs");
      if (this.map.getLayer("matrix_targets"))
        this.map.removeLayer("matrix_targets");
      if (this.map.getLayer("matrix_labels"))
        this.map.removeLayer("matrix_labels");
      if (this.map.getSource("matrix_targets"))
        this.map.removeSource("matrix_targets");

      this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
        field: "active",
        value: null
      });
      this.map.setFeatureState(
        { source: "zones", id: feature.id },
        { active: false }
      );
      return;
    }

    // get data and meta info
    const initialData = await this.getArcLayersDataById(properties.id);
    if (!initialData || !initialData.length) return;

    const data = initialData.map(f => ({
      ...f,
      geom: f.from_geom
    }));

    let min = data[0].value;
    let max = data[0].value;

    data.forEach(({ value }) => {
      if (value < min) min = value;
      if (value > max) max = value;
    });

    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "max",
      value: max
    });
    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "min",
      value: min
    });

    // target point layer (Mapbox circle layer)
    const targetData = jsonToGeojson(data);
    targetData.features = targetData.features.map(f => ({
      ...f,
      properties: {
        value: f.properties.value,
        label: f.properties.value.toFixed(2)
      }
    }));
    const source = this.map.getSource("matrix_targets");

    const radiusValue = [
      "interpolate",
      ["linear"],
      ["get", "value"],
      min,
      2,
      max,
      10
    ];

    if (!source) {
      this.map.addSource("matrix_targets", {
        type: "geojson",
        data: targetData
      });
      this.map.addLayer({
        id: "matrix_targets",
        source: "matrix_targets",
        type: "circle",
        paint: {
          "circle-color": "#f44949",
          "circle-opacity": 0.8,
          "circle-radius": radiusValue,
          "circle-pitch-alignment": "map"
        }
      });
    } else {
      this.map.setPaintProperty("matrix_targets", "circle-radius", radiusValue);
      source.setData(targetData);
    }

    // target symbol layer
    const labelsLayer = this.map.getLayer("matrix_labels");

    if (!labelsLayer) {
      this.map.addLayer({
        id: "matrix_labels",
        source: "matrix_targets",
        type: "symbol",
        paint: {
          "text-color": "#eee",
          "text-halo-color": "#202",
          "text-halo-width": 1
        },
        layout: {
          "text-field": ["get", "label"],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": 7,
          "text-letter-spacing": 0.05,
          "text-pitch-alignment": "map",
          "text-rotation-alignment": "map"
        }
      });
    }

    // arc layer (deck.gl ArcLayer)
    const layer = this.map.getLayer("matrix_arcs");

    const options = {
      getSourcePosition: d => d.from_geom.coordinates,
      getTargetPosition: d => d.to_geom.coordinates,
      getSourceColor: [244, 73, 73, 55],
      getTargetColor: d => {
        const { value } = d;

        const step = (max - min) / 4;

        if (value < min + step) return [46, 147, 254, 200];
        if (value < min + step * 2) return [85, 183, 126, 200];
        if (value < min + step * 3) return [248, 195, 72, 200];

        return [214, 56, 56, 200];
      },
      widthUnits: "pixels",
      widthScale: 1,
      getStrokeWidth: d => {
        const { value } = d;
        const part = (value - min) / (max - min);

        return part * 6;
      }
    };

    if (!layer) {
      const layer = new MapboxLayer({
        id: "matrix_arcs",
        type: ArcLayer,
        data,
        ...options
      });
      this.map.addLayer(layer);
    } else {
      layer.implementation.setProps({
        data,
        ...options
      });

      if (active) {
        this.map.setFeatureState(
          { source: "zones", id: active },
          { active: false }
        );
      }
    }

    // set active id state
    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "active",
      value: id
    });
    this.map.setFeatureState({ source: "zones", id }, { active: true });
  }

  addZonesHandlers() {
    // zone layer handlers
    this.map.on("mousemove", "zones", this.zoneMousemoveHandler);
    this.map.on("mouseleave", "zones", this.zoneMouseleaveHandler);
    this.map.on("click", "zones", this.zoneClickHandler);
  }

  // common togglers
  async addZones() {
    const data = await this.getZonesData();
    if (!data) return;

    this.addZonesMapLayer(data);
    this.addZonesHandlers();
  }

  removeZones() {
    // remove handlers
    this.map.off("mousemove", "zones", this.zoneMousemoveHandler);
    this.map.off("mouseleave", "zones", this.zoneMouseleaveHandler);
    this.map.off("click", "zones", this.zoneClickHandler);

    // remove layers and sources
    if (this.map.getLayer("zones")) this.map.removeLayer("zones");
    if (this.map.getLayer("zones_borders"))
      this.map.removeLayer("zones_borders");
    if (this.map.getSource("zones")) this.map.removeSource("zones");
    if (this.map.getLayer("matrix_arcs")) this.map.removeLayer("matrix_arcs");
    if (this.map.getLayer("matrix_targets"))
      this.map.removeLayer("matrix_targets");
    if (this.map.getLayer("matrix_labels"))
      this.map.removeLayer("matrix_labels");
    if (this.map.getSource("matrix_targets"))
      this.map.removeSource("matrix_targets");

    // clear store
    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "active",
      value: null
    });
    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "hover",
      value: null
    });
    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "max",
      value: 0
    });
    this.$store.commit("SET_SI_CENTER_MATRIX_PROP", {
      field: "min",
      value: 0
    });
  }
}
